import Button from "@/components/Button";
import { DateInput } from "@/components/InputFields";
import SkeletonTextLine from "@/components/SkeletonTextLine";
import Toast from "@/components/Toast";
import { useUpdateDeliveryDateMutation } from "@/data/queries";
import { DiabetesPatient, HypertensionPatient } from "@/model/Patient";
import cn from "@/util/cn";
import { formatDate } from "@/util/date-utils";
import { LocalDate, ZoneId } from "@js-joda/core";
import { ComponentPropsWithoutRef, useState } from "react";
import { toast } from "sonner";
import InfoBox from "./InfoBox";

type PatientInfoBoxProps<T extends DiabetesPatient | HypertensionPatient> = {
  patient: T | null;
  isLoading: boolean;
} & ComponentPropsWithoutRef<"div">;

const formatDeliveryDate = (date: LocalDate | undefined) =>
  date ? formatDate("MM/dd/yyyy", date) : "N/A";

const PatientInfoBox = <T extends DiabetesPatient | HypertensionPatient>({
  patient,
  isLoading,
  className,
  ...otherProps
}: PatientInfoBoxProps<T>) => {
  const deliveryDate = !isLoading && patient ? patient.deliveryDate : undefined;
  const [isEditingDeliveryDate, setIsEditingDeliveryDate] = useState(false);
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(deliveryDate);

  const { mutateAsync: updateDeliveryDate, isPending: isUpdatingDeliveryDate } =
    useUpdateDeliveryDateMutation();

  const handleDeliveryDateChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setSelectedDeliveryDate(newDate ? LocalDate.parse(newDate) : undefined);
  };

  const handleDeliveryDateClick = () => {
    if (!isUpdatingDeliveryDate) {
      setIsEditingDeliveryDate(true);
      setSelectedDeliveryDate(deliveryDate);
    }
  };

  const handleResetDeliveryDate = () => {
    setSelectedDeliveryDate(deliveryDate);
    setIsEditingDeliveryDate(false);
  };

  const handleSaveDeliveryDate = async () => {
    setIsEditingDeliveryDate(false);
    try {
      await updateDeliveryDate({
        patientId: patient!.id,
        deliveryDate: selectedDeliveryDate,
      });
      setIsEditingDeliveryDate(false);
    } catch {
      toast.custom((toastId) => (
        <Toast
          toastId={toastId}
          variant="error"
          description={"Something went wrong while updating the delivery date"}
        />
      ));
      setSelectedDeliveryDate(deliveryDate);
    }
  };

  return (
    <InfoBox className={className} {...otherProps}>
      <InfoBox.Header className="mb-2">
        <div className="flex items-center justify-between">
          <span>Patient information</span>
          {isEditingDeliveryDate && (
            <div className="flex gap-2">
              <Button
                variant="clear"
                onClick={handleResetDeliveryDate}
                className={cn("px-2 text-xs", selectedDeliveryDate === deliveryDate && "hidden")}
              >
                <span className="material-icons text-xs!">undo</span>
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveDeliveryDate}
                className={cn("px-2 text-xs", selectedDeliveryDate === deliveryDate && "hidden")}
              >
                <span className="material-icons text-xs!">check</span>
              </Button>
            </div>
          )}
        </div>
      </InfoBox.Header>
      <InfoBox.Lines className="gap-x-2">
        <InfoBox.Line
          label="MRN:"
          value={<Value value={patient?.medicalRecordNumber} isLoading={isLoading} />}
        />
        <InfoBox.Line
          label="Diagnosis:"
          value={<Value value={patient?.diagnosis} isLoading={isLoading} truncate={true} />}
        />
        <InfoBox.Line
          label="Phone number:"
          value={<Value value={patient?.phoneNumber} isLoading={isLoading} />}
        />
        <InfoBox.Line
          label="Prescribing provider:"
          value={<Value value={patient?.prescribingCaregiver?.displayName} isLoading={isLoading} />}
        />
        <InfoBox.Line
          label="Department:"
          value={
            <Value value={patient?.prescribingCaregiverDepartment?.name} isLoading={isLoading} />
          }
        />
        <InfoBox.Line
          label="Delivery date:"
          value={
            isLoading ? (
              <SkeletonTextLine className="mt-1 h-4 min-w-48" />
            ) : isEditingDeliveryDate ? (
              <DateInput
                value={selectedDeliveryDate?.toString()}
                onChange={handleDeliveryDateChange}
                className="h-8 w-32"
                max={LocalDate.now(ZoneId.SYSTEM).toString()}
              />
            ) : (
              <div className="flex items-center gap-2" onClick={handleDeliveryDateClick}>
                <Value
                  value={formatDeliveryDate(deliveryDate)}
                  isLoading={isLoading}
                  className={cn(isUpdatingDeliveryDate && "text-gray-400")}
                />
                {isUpdatingDeliveryDate ? (
                  <span className="material-icons animate-spin text-sm text-gray-400">
                    autorenew
                  </span>
                ) : (
                  <span
                    className="material-icons whitespace-no-wrap cursor-pointer select-none rounded-md p-[2px] text-primary/90 hover:bg-primary/10"
                    style={{ fontSize: "20px" }}
                  >
                    edit
                  </span>
                )}
              </div>
            )
          }
        />
      </InfoBox.Lines>
    </InfoBox>
  );
};

type ValueProps = {
  value?: string;
  isLoading: boolean;
  className?: string;
  /**
   * @default true
   * Truncation always happens when maxLines is set
   */
  truncate?: boolean;
  maxLines?: number | false;
};

const Value = ({ value, isLoading, className, truncate = true, maxLines }: ValueProps) =>
  isLoading ? (
    <SkeletonTextLine className="mt-1 h-4 min-w-48" />
  ) : (
    <p
      className={cn(
        "min-w-0",
        maxLines && `line-clamp-${maxLines}`,
        truncate ? "truncate" : "whitespace-normal wrap-break-word",
        className
      )}
      title={value as string}
    >
      {value ?? "N/A"}
    </p>
  );

export default PatientInfoBox;
